<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import LaisStatusBadge from 'dashboard/components-next/LaisStatusBadge.vue';
import LeadAvatar from './LeadAvatar.vue';
import LeadContextBadges from './LeadContextBadges.vue';
import LeadsListSkeleton from './LeadsListSkeleton.vue';
import LeadsListState from './LeadsListState.vue';

defineProps({
  isLoading: { type: Boolean, default: false },
  loadError: { type: Boolean, default: false },
  leads: { type: Array, default: () => [] },
  selected: { type: Set, default: () => new Set() },
  allSelected: { type: Boolean, default: false },
  selectedCount: { type: Number, default: 0 },
  selectedCountLabel: { type: String, default: '' },
  selectionHint: { type: String, default: '' },
  isRemarketingSelectionOutOfRange: { type: Boolean, default: false },
  hasAnyLeadFilter: { type: Boolean, default: false },
  canSendLeadCrm: { type: Boolean, default: false },
  canSendLeadCrmAction: { type: Boolean, default: false },
  canUseLeadQuickView: { type: Boolean, default: false },
  canManageLeadRecords: { type: Boolean, default: false },
  canManageLeadRecordsAction: { type: Boolean, default: false },
  canDeleteLeadRecords: { type: Boolean, default: false },
  canDeleteLeadRecordsAction: { type: Boolean, default: false },
  statusMeta: { type: Object, default: () => ({}) },
  crmSummary: { type: Function, required: true },
  crmStatusMeta: { type: Function, required: true },
  leadStats: { type: Function, required: true },
});

const emit = defineEmits([
  'toggle-all',
  'toggle-one',
  'retry',
  'reset-filters',
  'view-history',
  'open-crm',
  'open-timeline',
  'open-quick-view',
  'copy-link',
  'edit-lead',
  'delete-lead',
]);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div
    v-if="leads.length"
    class="flex flex-wrap items-center gap-x-4 gap-y-2 py-2 text-[13px] text-lais-text-body"
  >
    <label class="inline-flex cursor-pointer items-center gap-2">
      <input
        type="checkbox"
        :checked="allSelected"
        class="size-4 accent-lais-primary"
        @change="emit('toggle-all')"
      />
      <span>{{ allSelected ? 'Desmarcar todos' : 'Selecionar todos' }}</span>
    </label>
    <span
      v-if="selectedCount"
      class="inline-flex flex-wrap items-center gap-2 text-lais-text-muted"
    >
      <span>{{ selectedCountLabel }}</span>
      <span
        v-if="selectionHint"
        class="rounded-full px-2 py-0.5 text-[11px] font-semibold"
        :class="
          isRemarketingSelectionOutOfRange
            ? 'bg-amber-50 text-amber-700'
            : 'bg-lais-bg-muted text-lais-text-muted'
        "
      >
        {{ selectionHint }}
      </span>
    </span>
  </div>

  <LeadsListSkeleton v-if="isLoading && leads.length === 0" :rows="4" />
  <LeadsListState
    v-else-if="loadError && leads.length === 0"
    title="Não foi possível carregar os clientes"
    description="Atualize a lista para tentar novamente."
    icon="i-lucide-circle-alert"
    action-label="Tentar novamente"
    critical
    @action="emit('retry')"
  />
  <LeadsListState
    v-else-if="leads.length === 0"
    :title="
      hasAnyLeadFilter
        ? 'Nenhum cliente encontrado'
        : 'Nenhum cliente recebido ainda'
    "
    :description="
      hasAnyLeadFilter
        ? 'Nenhum cliente corresponde aos filtros aplicados.'
        : 'Os clientes que entrarem em contato com a Lais aparecerão aqui.'
    "
    :action-label="hasAnyLeadFilter ? 'Limpar filtros' : ''"
    :icon="hasAnyLeadFilter ? 'i-lucide-search-x' : 'i-lucide-users'"
    @action="emit('reset-filters')"
  />

  <ul v-else role="list" class="grid gap-3 pb-4">
    <li v-for="lead in leads" :key="lead.id">
      <article
        class="flex flex-col gap-3 rounded-lais border bg-lais-bg-card p-4 shadow-lais-card transition hover:border-lais-primary/40"
        :class="
          selected.has(lead.id)
            ? 'border-lais-primary/60 ring-1 ring-lais-primary/20'
            : 'border-lais-border-soft'
        "
      >
        <div class="flex items-start gap-3">
          <input
            type="checkbox"
            :checked="selected.has(lead.id)"
            class="mt-1 size-4 shrink-0 accent-lais-primary"
            aria-label="Selecionar cliente"
            @change="emit('toggle-one', lead.id)"
          />
          <LeadAvatar :lead="lead" size-class="size-10" />
          <div class="min-w-0 flex-1">
            <button
              type="button"
              class="block max-w-full truncate text-left font-redHatDisplay text-[15px] font-semibold text-lais-text hover:text-lais-primary"
              @click="emit('view-history', lead.id)"
            >
              {{ lead.name || 'Cliente anônimo' }}
            </button>
            <p class="mt-0.5 truncate text-[12px] text-lais-text-muted">
              <span v-if="lead.phone_number || lead.email">
                {{ lead.phone_number || lead.email }}
              </span>
              <span v-else>Contato não informado</span>
            </p>
          </div>
          <button
            type="button"
            class="hidden size-8 shrink-0 place-items-center rounded-full text-lais-text-muted transition-colors hover:bg-lais-primary-50 hover:text-lais-primary sm:grid"
            title="Abrir histórico do cliente"
            aria-label="Abrir histórico do cliente"
            @click="emit('view-history', lead.id)"
          >
            <span class="i-lucide-chevron-right size-4" aria-hidden="true" />
          </button>
        </div>

        <div class="flex flex-wrap items-center gap-1.5">
          <LaisStatusBadge
            size="sm"
            :tone="statusMeta[lead.qualification_status]?.tone || 'neutral'"
            :label="
              statusMeta[lead.qualification_status]?.label || 'Sem status'
            "
          />
          <LaisStatusBadge
            size="sm"
            :tone="crmStatusMeta(lead).tone"
            icon="i-lucide-refresh-ccw"
            :label="`CRM: ${crmStatusMeta(lead).label}`"
          />
          <LaisStatusBadge
            v-if="lead.blocked || lead.blocked_contact"
            size="sm"
            tone="danger"
            icon="i-lucide-ban"
            label="Bloqueado"
          />
          <LeadContextBadges :lead="lead" include-stage />
          <LaisStatusBadge
            v-if="lead.additional_attributes?.operacao"
            size="sm"
            tone="neutral"
            icon="i-lucide-tag"
            :label="lead.additional_attributes.operacao"
          />
          <LaisStatusBadge
            v-if="lead.additional_attributes?.canal"
            size="sm"
            tone="neutral"
            icon="i-lucide-radio"
            :label="lead.additional_attributes.canal"
          />
        </div>

        <dl class="grid grid-cols-2 gap-x-4 gap-y-1 text-[12px] sm:grid-cols-3">
          <div class="flex items-baseline gap-1">
            <dt class="text-lais-text-muted">Visitas</dt>
            <dd class="font-semibold text-lais-text tabular-nums">
              {{ leadStats(lead).visits }}
            </dd>
          </div>
          <div class="flex items-baseline gap-1">
            <dt class="text-lais-text-muted">Propostas</dt>
            <dd class="font-semibold text-lais-text tabular-nums">
              {{ leadStats(lead).offers }}
            </dd>
          </div>
          <div class="flex min-w-0 items-baseline gap-1">
            <dt class="shrink-0 text-lais-text-muted">Última atividade</dt>
            <dd class="truncate font-medium text-lais-text">
              {{ leadStats(lead).lastActivity || 'Sem registro' }}
            </dd>
          </div>
        </dl>

        <p
          v-if="crmSummary(lead).last_error"
          class="rounded-lais-sm bg-lais-danger-50 px-2 py-1 text-[11px] text-lais-danger"
        >
          {{ crmSummary(lead).last_error }}
        </p>

        <div
          class="flex flex-wrap items-center gap-2 border-t border-lais-border-soft pt-3"
        >
          <button
            v-if="canSendLeadCrm"
            type="button"
            class="inline-flex h-8 items-center gap-1.5 rounded-lais-sm border border-lais-border-soft px-3 text-[12px] font-semibold text-lais-primary hover:border-lais-primary hover:bg-lais-primary-50 disabled:cursor-not-allowed disabled:opacity-50"
            :disabled="!canSendLeadCrmAction"
            @click="emit('open-crm', lead.id)"
          >
            <span class="i-lucide-send size-3.5" aria-hidden="true" />
            Enviar ao CRM
          </button>
          <button
            type="button"
            class="inline-flex h-8 items-center gap-1.5 rounded-lais-sm border border-lais-border-soft px-3 text-[12px] font-semibold text-lais-text-body hover:border-lais-primary hover:text-lais-primary"
            aria-label="Abrir timeline CRM"
            @click="emit('open-timeline', lead)"
          >
            <span class="i-lucide-history size-3.5" aria-hidden="true" />
            Timeline CRM
          </button>
          <button
            v-if="canUseLeadQuickView"
            type="button"
            class="inline-flex h-8 items-center gap-1.5 rounded-lais-sm border border-lais-border-soft px-3 text-[12px] font-semibold text-lais-text-body hover:border-lais-primary hover:text-lais-primary"
            @click="emit('open-quick-view', lead)"
          >
            <span
              class="i-lucide-panel-right-open size-3.5"
              aria-hidden="true"
            />
            Visão rápida
          </button>
          <button
            type="button"
            class="inline-flex h-8 items-center gap-1.5 rounded-lais-sm border border-lais-border-soft px-3 text-[12px] font-semibold text-lais-text-body hover:border-lais-primary hover:text-lais-primary"
            @click="emit('copy-link', lead.id)"
          >
            <span class="i-lucide-link size-3.5" aria-hidden="true" />
            Copiar link
          </button>
          <button
            v-if="canManageLeadRecords"
            type="button"
            class="inline-flex h-8 items-center gap-1.5 rounded-lais-sm border border-lais-border-soft px-3 text-[12px] font-semibold text-lais-text-body hover:border-lais-primary hover:text-lais-primary disabled:cursor-not-allowed disabled:opacity-50"
            :disabled="!canManageLeadRecordsAction"
            @click="emit('edit-lead', lead)"
          >
            <span class="i-lucide-pencil size-3.5" aria-hidden="true" />
            Editar
          </button>
          <button
            v-if="canDeleteLeadRecords"
            type="button"
            class="inline-flex h-8 items-center gap-1.5 rounded-lais-sm border border-lais-danger/30 px-3 text-[12px] font-semibold text-lais-danger hover:bg-lais-danger-50 disabled:cursor-not-allowed disabled:opacity-50"
            :disabled="!canDeleteLeadRecordsAction"
            @click="emit('delete-lead', lead)"
          >
            <span class="i-lucide-trash-2 size-3.5" aria-hidden="true" />
            Excluir
          </button>
        </div>
      </article>
    </li>
  </ul>
</template>
